import React, { useState, useEffect, useContext } from 'react';
import Axios from 'axios';
import { rankInspectionRequests } from '../calculations/sortInspections';
import { getAvailableInspectorsForDate } from '../calculations/getInspectorAvailability';
import { assignInspectionsToInspectors } from '../calculations/assignInspections';
import { optimizeInspectorRoutes } from '../calculations/optimizeInspections';
import { calculateETAs } from '../calculations/calculateETAs';
import InspectorSelection from './components/InspectorSelection';
import InspectionSelection from './components/InspectionSelection';
import { AuthContext } from '../../AuthContext';
import './SelectionDashboard.css';

function SelectionDashboard({ inspections, inspectors, selectedDate, setOptimizedRoutes, setFailedAssignments, setPage, setIsFromRouteCreation }) {
    const { storeDOBFailedRoutes } = useContext(AuthContext);
    const [sortedInspections, setSortedInspections] = useState([]);
    const [availableInspectors, setAvailableInspectors] = useState([]);
    const [selectedInspectors, setSelectedInspectors] = useState([]);
    const [selectedInspections, setSelectedInspections] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [holidayInfo, setHolidayInfo] = useState(null);
    const [isOptimizing, setIsOptimizing] = useState(false);
    const [optimizationProgress, setOptimizationProgress] = useState({
        currentRoute: 0,
        totalRoutes: 0,
        currentInspectorId: null
    });


    // console.log('selectedInspectors', selectedInspectors)
    // console.log('selectedInspections', selectedInspections)

    // Sort inspections and get available inspectors on component mount
    useEffect(() => {
        const processData = async () => {
            setIsLoading(true);
            setHolidayInfo(null);

            // Wrap the processing in a Promise to ensure loading state is shown
            await new Promise(resolve => {
                setTimeout(() => {
                    // Get today's date
                    // For testing, you can set a specific date
                    // const [year, month, day] = '2025-1-24'.split('-').map(Number);
                    // const today = new Date(year, month - 1, day, 12, 0, 0);
                    const today = selectedDate; // Use passed in selectedDate which is already in correct Date format
                    
                    // Check if it's a holiday first
                    const holiday = inspectors.holidayListData?.find(h => {
                        const holDate = new Date(h.START_DATE);
                        return holDate.getFullYear() === today.getFullYear() &&
                               holDate.getMonth() === today.getMonth() &&
                               holDate.getDate() === today.getDate();
                    });

                    if (holiday) {
                        setHolidayInfo(holiday);
                        setAvailableInspectors([]);
                    } else {
                        // Get available inspectors for today
                        const available = getAvailableInspectorsForDate(
                            inspectors.inspectorsData || [],
                            inspectors.calendarData || [],
                            inspectors.holidayListData || [],
                            today
                        );

                        const availableWithDistricts = addDistrictCodesToInspectors(available, inspectors.districtData);

                        // setAvailableInspectors(availableWithDistricts);
                        setAvailableInspectors(addDistrictCodesToInspectors(inspectors.inspectorsData, inspectors.districtData)); // testing w/o checking calendars/holidays
                    }

                    // Process inspections
                    if (inspections.inspectionsData) {
                        const ranked = rankInspectionRequests(inspections.inspectionsData, {
                            locationTieBreak: false,
                            selectedDate: today
                        });

                        const rankedWithDuration = addImportedDurationToInspections(ranked, inspections.inspectionDurationData);

                        setSortedInspections(rankedWithDuration);
                    }

                    resolve();
                }, 0);
            });

            setIsLoading(false);
        };

        processData();
    }, [selectedDate, inspections.inspectionsData, inspectors.inspectorsData, inspectors.calendarData, inspectors.holidayListData]);


    // Helper function to add district codes to inspectors
    function addDistrictCodesToInspectors(inspectors, districtData) {
        if (!districtData) return inspectors;

        return inspectors.map(inspector => {
            // Find matching district data
            const inspectorDistrict = districtData.find(
                d => d.INSPECTOR_ID === inspector.INSPECTOR_ID
            );

            // If found, split the district string into an array, handling spaces
            const districtCodes = inspectorDistrict?.District
                ? inspectorDistrict.District.split('|')
                    .map(code => code.trim()) // Remove any spaces
                    .filter(code => code.length > 0) // Remove empty strings
                : [];

            // Return inspector with added districtCodes
            return {
                ...inspector,
                districtCodes
            };
        });
    }

    function addImportedDurationToInspections(inspections, durationData) {
        return inspections.map(inspection => {
            // Extract category code before the "-", accounting for spaces
            const categoryCode = inspection.ComplaintCategory.split('-')[0].trim();
            
            // Find matching duration data
            const matchedDuration = durationData?.find(
                d => d['Complaint Category'] === categoryCode
            );

            // Convert duration string to integer, fallback to existing DURATION if no match
            const duration = matchedDuration 
                ? parseInt(matchedDuration['Inspection Duration'], 10)
                : parseInt(inspection.DURATION, 10);

            // Add importedDuration as a number (not a string)
            return {
                ...inspection,
                importedDuration: isNaN(duration) ? 1800 : duration // default to 30 minutes (1800 seconds) if parsing fails
            };
        });
    }

    
    // Handle inspector selection
    const handleInspectorSelect = (inspectorId, inspectorIdsArray = null) => {
        if (holidayInfo) return; // Prevent selection on holidays
        
        // Handle special cases for select all/deselect all
        if (inspectorId === 'select-none') {
            setSelectedInspectors([]);
            return;
        }
        
        if (inspectorId === 'select-all' && inspectorIdsArray) {
            setSelectedInspectors(inspectorIdsArray);
            return;
        }
        
        setSelectedInspectors(prev => {
            if (prev.includes(inspectorId)) {
                return prev.filter(id => id !== inspectorId);
            }
            return [...prev, inspectorId];
        });
    };

    // Handle inspection selection
    const handleInspectionSelect = (inspectionId) => {
        if (holidayInfo) return; // Prevent selection on holidays
        setSelectedInspections(prev => {
            if (prev.includes(inspectionId)) {
                return prev.filter(id => id !== inspectionId);
            }
            return [...prev, inspectionId];
        });
    };

    async function goRoutes() {
        // Get full details of selected inspectors
        const selectedInspectorsDetails = availableInspectors.filter(inspector => 
            selectedInspectors.includes(inspector.INSPECTOR_ID)
        );

        // Get full details of selected inspections NOT CURRENTLY RELEVANT
        const selectedInspectionsDetails = sortedInspections.filter(inspection => 
            selectedInspections.includes(inspection.INSPECTION_ID)
        );

        // Split inspections into scheduled and unscheduled
        const prescheduledInspections = sortedInspections.filter(inspection =>
            inspection.Status === "Scheduled"
        );
        const unscheduledInspections = sortedInspections.filter(inspection => 
            inspection.Status !== "Scheduled"
        );

        console.log('Selected Date:', selectedDate);
        console.log('Unscheduled Inspections:', unscheduledInspections);
        console.log('Prescheduled Inspections:', prescheduledInspections);

        // Assign inspections to inspectors
        const assignments = assignInspectionsToInspectors(selectedDate, selectedInspectorsDetails, unscheduledInspections, prescheduledInspections);

        console.log('Selected Inspectors Details:', selectedInspectorsDetails);
        console.log('Assignment Results:', assignments);

        // Start optimization with progress tracking
        setIsOptimizing(true);
        setOptimizationProgress({
            currentRoute: 0,
            totalRoutes: assignments.length,
            currentInspectorId: null
        });

        // optimize routes with progress callback
        const optimizationResult = await optimizeInspectorRoutes(
            assignments, 
            selectedDate,
            (currentRoute, totalRoutes, inspectorId) => {
                setOptimizationProgress({
                    currentRoute,
                    totalRoutes,
                    currentInspectorId: inspectorId
                });
            }
        );
        
        const optimizedRoutesFinal = optimizationResult.optimizedAssignments;
        const failedAssignments = optimizationResult.failedAssignments;
        
        console.log('Optimized Routes with ETAs:', optimizedRoutesFinal);
        console.log('Failed Assignments:', failedAssignments);

        if (failedAssignments.length > 0) {
            // Store failed assignments in Firebase for debugging purposes
            try {
                await storeDOBFailedRoutes(failedAssignments);
                console.log('Failed assignments stored in Firebase for debugging');
            } catch (error) {
                console.error('Error storing failed assignments:', error);
            }
        }

        try {
           // for logging purposes
            let rankArray = [];
            for (let a = 0; a < optimizedRoutesFinal.length; a++) {
                let currInspections = optimizedRoutesFinal[a].assignedInspections;
                for (let b = 0; b < currInspections.length; b++) {
                    rankArray.push(currInspections[b].rank);
                }
            }
            rankArray.sort((a, b) => a - b); // Sort numerically in ascending order
            console.log('Rank Array:', rankArray); 
        } catch (error) {
            console.error('Error calculating rank array:', error);
        }

        // Complete optimization and navigate immediately
        setIsOptimizing(false);
        setOptimizedRoutes(optimizedRoutesFinal);
        setFailedAssignments(failedAssignments);
        setIsFromRouteCreation(true); // Set flag to prevent database retrieval
        setPage('routes');

        // Save optimized routes to database in background
        const saveRoutesInBackground = async () => {
            try {
                for (let i = 0; i < optimizedRoutesFinal.length; i++) {
                    const inspectorRoute = optimizedRoutesFinal[i];
                    const isNewDate = i === 0;
                    
                    const response = await Axios.post(`https://api.routora.com/save-inspector-routes-chunked`, {
                        inspectorRoute: inspectorRoute,
                        selectedDate: selectedDate.toISOString().split('T')[0],
                        index: i,
                        isNewDate: isNewDate
                    });
                    
                    console.log(`Route ${i + 1} saved successfully:`, response.data);
                }
                console.log('All routes saved successfully');
            } catch (error) {
                console.error('Error saving routes to database:', error);
                // Optionally show a notification about save error
            }
        };

        // Start background saving
        saveRoutesInBackground();
    }
    


    if (isLoading) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-white">
                <div className="text-center">
                    <div className="spinner-inspections">
                        <div className="spinner-outer-circle"></div>
                        <div className="spinner-inner-circle"></div>
                    </div>
                    <h3 className="mt-8 text-xl font-medium text-gray-900">Processing Data</h3>
                    <p className="mt-2 text-sm text-gray-500">
                        Please wait while we sort the inspection data and retrieve available inspectors...
                    </p>
                </div>
            </div>
        );
    }

    if (isOptimizing) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-white">
                <div className="text-center max-w-md mx-auto px-6">
                    <div className="spinner-inspections mb-8">
                        <div className="spinner-outer-circle"></div>
                        <div className="spinner-inner-circle"></div>
                    </div>
                    
                    <h3 className="text-xl font-medium text-gray-900 mb-4">
                        Optimizing Routes
                    </h3>
                    
                    <div className="mb-6">
                        <div className="flex justify-between text-sm text-gray-600 mb-2">
                            <span>Progress</span>
                            <span>{optimizationProgress.currentRoute} of {optimizationProgress.totalRoutes}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                                style={{ 
                                    width: `${optimizationProgress.totalRoutes > 0 ? (optimizationProgress.currentRoute / optimizationProgress.totalRoutes) * 100 : 0}%` 
                                }}
                            ></div>
                        </div>
                    </div>
                    
                    {optimizationProgress.currentInspectorId && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                            <div className="flex items-center">
                                <svg className="h-5 w-5 text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                <span className="text-sm font-medium text-blue-800">
                                    Optimizing Inspector {optimizationProgress.currentInspectorId}
                                </span>
                            </div>
                        </div>
                    )}
                    
                    <p className="text-sm text-gray-500">
                        Please wait while our engine does the work for you.
                    </p>
                    
                    <div className="mt-6 text-xs text-gray-400">
                        This may take a few moments depending on the number of routes
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col pt-20">
            <div className="flex-1 flex px-8">
                {/* Left panel - Inspector Selection */}
                <div style={{width: "700px"}} className="h-full pr-4">
                    {holidayInfo ? (
                        <div>
                            <InspectorSelection
                                availableInspectors={availableInspectors}
                                inspectorsData={inspectors.inspectorsData}
                                selectedInspectors={selectedInspectors}
                                onInspectorSelect={handleInspectorSelect}
                            />
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 mt-4">
                                <div className="flex items-center">
                                    <svg className="h-5 w-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <h3 className="text-lg font-medium text-red-800">Holiday Notice</h3>
                                </div>
                                <p className="mt-2 text-sm text-red-700">
                                    {holidayInfo.HOLIDAY_NAME} - No inspectors are available on this holiday.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <InspectorSelection
                            availableInspectors={availableInspectors}
                            inspectorsData={inspectors.inspectorsData}
                            selectedInspectors={selectedInspectors}
                            onInspectorSelect={handleInspectorSelect}
                        />
                    )}
                </div>

                {/* Right panel - Inspection Selection */}
                <div className="w-full h-full pl-4 pb-5">
                    <InspectionSelection
                        sortedInspections={sortedInspections}
                        selectedInspections={selectedInspections}
                        onInspectionSelect={handleInspectionSelect}
                        goRoutes={goRoutes}
                    />
                </div>
            </div>
        </div>
    );
}

export default SelectionDashboard;